<template>
    <div class="date-picker-wrapper">
        <!-- Days Column -->
        <div class="top-line"></div>
        <div
        class="day-wrapper"
        ref="dayPicker"
        @touchstart="isPressingDay = true"
        @touchend="[isPressingDay = false, adjustDayScrollHandler()]"
        @scroll="dayValueScrolled = $event.target.scrollTop">
            <div v-for="(day, index) in daysList"
            :key="`${day.id}_${index}`">
                {{ day.name }}
            </div>
        </div>
        <div class="bottom-line"></div>
        <!-- Months Column -->
        <div class="top-line month"></div>
        <div class="month-wrapper"
        ref="monthPicker"
        @touchstart="isPressingMonth = true"
        @touchend="[isPressingMonth = false, adjustMonthScrollHandler()]"
        @scroll="monthValueScrolled = $event.target.scrollTop">
            <div v-for="(month, index) in monthList"
            :key="`${month.name}_${index}`">
                {{ month.name }}
            </div>
        </div>
        <div class="bottom-line month"></div>
        <!-- Years Column -->
        <div class="top-line year"></div>
        <div class="year-wrapper"
        ref="yearPicker"
        @touchstart="isPressingYear = true"
        @touchend="[isPressingYear = false, adjustYearScrollHandler()]"
        @scroll="yearValueScrolled = $event.target.scrollTop">
            <div v-for="(year, index) in yearList"
            :key="`${year.id}_${index}`">
                {{ year.name }}
            </div>
        </div>
        <div class="bottom-line year"></div>
    </div>
</template>

<script setup>
import { ref, nextTick, watch, onMounted, computed } from 'vue'
import moment from '@/assets/setups/moment'

/* Emmitters */
const emit = defineEmits(['dateUpdate'])

const props = defineProps({
    selectedDate: Date,
    disabledDates: Object
})

/**
 * *************** DAY HANDLERS ***************
*/
const daysList = ref([])
const dayPicker = ref('dayPicker')
const xDay = ref()
const dayValueScrolled = ref(0)
const dayPickerWaitingTime = ref(null)
const isPressingDay = ref(false)
const fromAdjustDay = ref(false)

const setDaysList = () => {
    let startDay = 1
    let endDay = moment(new Date(xYear.value, xMonth.value)).daysInMonth() || 31

    if (props.disabledDates) {
        if (Object.hasOwn(props.disabledDates, 'to') && props.disabledDates.to && props.disabledDates.to.getMonth() === xMonth.value && props.disabledDates.to.getFullYear().toString() === xYear.value) {
            startDay = props.disabledDates.to.getDate()
        }

        if (Object.hasOwn(props.disabledDates, 'from') && props.disabledDates.from && props.disabledDates.from.getMonth() === xMonth.value && props.disabledDates.from.getFullYear().toString() === xYear.value) {
            endDay = props.disabledDates.from.getDate()
        }
    }

    daysList.value = [{
        id: 'dayTopBlock',
        name: ''
    }]

    for (let i = startDay; i <= endDay; i++) {
        daysList.value.push({
            id: i,
            name: i
        })
    }

    daysList.value.push({
        id: 'dayBottomBlock',
        name: ''
    })
}

watch(() => dayValueScrolled.value, (newValue, oldValue) => {
    if (fromAdjustDay.value) return
    if (dayPickerWaitingTime.value) clearTimeout(dayPickerWaitingTime.value)

    dayPickerWaitingTime.value = setTimeout(() => {
        if (!isPressingDay.value) adjustDayScrollHandler()
    }, 100)
})

const adjustDayScrollHandler = (day, instant) => {
    let index
    index = day ? daysList.value.findIndex(ele => ele.id.toString() === day.toString()) - 1 : Math.round(dayValueScrolled.value / 54)

    if (index < 0) {
        if (day < daysList.value[1].id) index = 0
        if (day > daysList.value[daysList.value.length - 2].id) index = daysList.value.length - 3
    }

    dayPicker.value.scrollTo({
        left: 0,
        top: dayPicker.value.children[index].offsetTop - dayPicker.value.offsetTop,
        behavior: instant ? 'instant' : 'smooth'
    })

    for (const ele of dayPicker.value.children) {
        if (ele.innerHTML === dayPicker.value.children[index + 1].innerHTML) {
            ele.classList.add('selected')
            xDay.value = ele.innerHTML
            emit('dateUpdate', getDate.value)
        } else {
            ele.classList.remove('selected')
        }
    }
}

/**
 * *************** MONTH HANDLERS ***************
*/
const monthList = ref([])
const monthPicker = ref('monthPicker')
const xMonth = ref()
const monthValueScrolled = ref(0)
const monthPickerWaitingTime = ref(null)
const isPressingMonth = ref(false)

const setMonthList = () => {
    const months = moment.months()

    let startMonth = 0
    let endMonth = 11

    if (props.disabledDates) {
        if (Object.hasOwn(props.disabledDates, 'to') && props.disabledDates.to && props.disabledDates.to.getFullYear().toString() === xYear.value) {
            startMonth = props.disabledDates.to.getMonth()
        }

        if (Object.hasOwn(props.disabledDates, 'from') && props.disabledDates.from && props.disabledDates.from.getFullYear().toString() === xYear.value) {
            endMonth = props.disabledDates.from.getMonth()
        }
    }

    monthList.value = [{
        id: 'monthTopBlock',
        name: ''
    }]

    months.forEach((month, index) => {
        if (index < startMonth || index > endMonth) return
        monthList.value.push({
            name: moment().month(month).format('MMM'),
            id: index
        })
    })

    monthList.value.push({
        id: 'monthBottomBlock',
        name: ''
    })
}

watch(() => monthValueScrolled.value, (newValue, oldValue) => {
    if (monthPickerWaitingTime.value) clearTimeout(monthPickerWaitingTime.value)

    monthPickerWaitingTime.value = setTimeout(() => {
        if (!isPressingMonth.value) adjustMonthScrollHandler()
    }, 100)
})

const adjustMonthScrollHandler = (month, instant) => {
    let index
    index = month ? monthList.value.findIndex(ele => ele.id === month) - 1 : Math.round(monthValueScrolled.value / 54)

    if (index < 0) {
        if (month < monthList.value[1].id) index = 0
        if (month > monthList.value[monthList.value.length - 2].id) index = monthList.value.length - 3
    }

    monthPicker.value.scrollTo({
        left: 0,
        top: monthPicker.value.children[index].offsetTop - monthPicker.value.offsetTop,
        behavior: instant ? 'instant' : 'smooth'
    })

    for (const ele of monthPicker.value.children) {
        if (ele.innerHTML === monthPicker.value.children[index + 1].innerHTML) {
            ele.classList.add('selected')
            xMonth.value = monthList.value.find(month => month.name === ele.innerHTML).id
            emit('dateUpdate', getDate.value)
        } else ele.classList.remove('selected')
    }

    setDaysList()
    nextTick(() => {
        adjustDayScrollHandler(xDay.value, true)
    })
}

/**
 * *************** YEAR HANDLERS ***************
*/
const yearList = ref([])
const yearPicker = ref('yearPicker')
const xYear = ref()
const yearValueScrolled = ref(0)
const yearPickerWaitingTime = ref(null)
const isPressingYear = ref(false)

const setYearList = () => {
    const newYear = new Date().getFullYear()

    let startYear = newYear - 100
    let endYear = newYear + 100

    if (props.disabledDates) {
        if (Object.hasOwn(props.disabledDates, 'to') && props.disabledDates.to) startYear = props.disabledDates.to.getFullYear()

        if (Object.hasOwn(props.disabledDates, 'from') && props.disabledDates.from) endYear = props.disabledDates.from.getFullYear()
    }

    for (let i = startYear; i <= endYear; i++) {
        yearList.value.push({
            id: i,
            name: i
        })
    }

    yearList.value.unshift({
        id: 'yearTopBlock',
        name: ''
    })

    yearList.value.push({
        id: 'yearBottomBlock',
        name: ''
    })
}

watch(() => yearValueScrolled.value, (newValue, oldValue) => {
    if (yearPickerWaitingTime.value) clearTimeout(yearPickerWaitingTime.value)

    yearPickerWaitingTime.value = setTimeout(() => {
        if (!isPressingYear.value) adjustYearScrollHandler()
    }, 100)
})

const adjustYearScrollHandler = (year) => {
    const index = year ? yearList.value.findIndex(ele => ele.id === year) - 1 : Math.round(yearValueScrolled.value / 54)

    yearPicker.value.scrollTo({
        left: 0,
        top: yearPicker.value.children[index].offsetTop - yearPicker.value.offsetTop,
        behavior: 'smooth'
    })

    for (const ele of yearPicker.value.children) {
        if (ele.innerHTML === yearPicker.value.children[index + 1].innerHTML) {
            ele.classList.add('selected')
            xYear.value = ele.innerHTML
            emit('dateUpdate', getDate.value)
        } else ele.classList.remove('selected')
    }

    setMonthList()
    nextTick(() => {
        adjustMonthScrollHandler(xMonth.value, true)
    })
}

const getDate = computed(() => {
    return new Date(xYear.value, xMonth.value, xDay.value)
})

onMounted(() => {
    const date = props.selectedDate ? new Date(props.selectedDate) : new Date()

    xYear.value = date.getFullYear()
    setYearList()
    xMonth.value = date.getMonth()
    xDay.value = date.getDate()
    setDaysList()

    nextTick(() => {
        adjustYearScrollHandler(xYear.value)
    })
})
</script>

<style lang="scss" scoped>
.date-picker-wrapper {
    display: flex;
    position: relative;
    gap: 8px;

    .day-wrapper, .month-wrapper, .year-wrapper {
        height: calc(3 * 54px);
        overflow: auto;

        div {
            height: 54px;
            width: 54px;

            display: flex;
            align-items: center;
            justify-content: center;

            color: black;
            opacity: 0.4;
            text-transform: capitalize;

            &.selected {
                opacity: 1;
                font-weight: 500;
            }
        }
    }

    .top-line {
        width: 54px;
        height: 2px;

        background-color: black;

        position: absolute;
        top: 54px;
        z-index: 1;

        &.month {
            left: 62px;
        }

        &.year {
            right: 0;
        }
    }

    .bottom-line {
        width: 54px;
        height: 2px;

        background-color: black;

        position: absolute;
        bottom: 54px;
        z-index: 1;

        &.month {
            left: 62px;
        }

        &.year {
            right: 0;
        }
    }
}
</style>
